/**
 * Web DB module — WatermelonDB on the LokiJS adapter, persisting to IndexedDB.
 *
 * The schema and model classes are byte-identical to BigxBookApp/src/db/* and
 * BigxBookDesktop/src/db/* per the AGENTS.md schema-lock policy. Only this
 * file differs, and it differs in one way that matters:
 *
 * ── Per-store databases ──────────────────────────────────────────────────────
 * Desktop has a single fixed `activeStoreId` and therefore one Database created
 * at module load. Web supports a store switcher (BIGXBOOK_WEB_PLAN §6), so a
 * merchant with several stores can hold several caches in one browser. Each
 * store gets its own IndexedDB — `bigxbook-web-<storeId>` — so data can never
 * bleed across stores.
 *
 * Ported modules do `import { database } from '@/db'` and call it at module
 * scope, exactly as they do on Desktop. To keep those ports byte-identical
 * (Decision 1), `database` is a Proxy that forwards every access to whichever
 * store is currently active. Call `activateStore(storeId)` before any DB use;
 * touching `database` beforehand throws a clear error rather than silently
 * reading the wrong store.
 *
 * This file is a DIVERGED entry in the drift-check — no byte comparison with
 * Desktop is intended.
 */
import { Database, Q } from '@nozbe/watermelondb';
// The sync internals. `fetchLocalChanges` is what synchronize() itself uses to
// decide what to push, so nothing can disagree with it about what is pending.
import { fetchLocalChanges } from '@nozbe/watermelondb/sync/impl'
import LokiJSAdapter from '@nozbe/watermelondb/adapters/lokijs';

import schema from './schema';
import migrations from './migrations';
import ErrorTracking from '../services/ErrorTrackingService';
import { setDbError } from './dbHealth';

// Domain 1: Core
import Merchant from './models/Merchant';
import Store from './models/Store';
import User from './models/User';
import UserInvitation from './models/UserInvitation';

// Domain 2: Products & Inventory
import {
  Product, Category, ProductVariant, Batch,
  Godown, StockMovement, Supplier, PurchaseOrder, PoLineItem,
  SupplierPayment, PurchaseReturn, PurchaseReturnItem, SupplierLedger,
  StockTransfer, StockTransferItem,
} from './models/Inventory';

// Domain 3: Billing
import { Invoice, LineItem, Payment, BillingSession } from './models/Billing';

// Domain 4: Customers & Loyalty
import { Customer, CustomerGroup, LoyaltyTier, LoyaltyTransaction } from './models/Customers';

// Domain 5–10: Pricing, Compliance, Staff, Finance, Devices, System
import {
  TaxProfile, PriceList, PriceListItem, Promotion,
  GstRateSlab, IndianState, HsnMaster, GstFiling, EInvoice, EWayBill,
  StaffAssignment, Shift, Attendance, Commission,
  ExpenseCategory, Expense,
  Device, AuditLog, Setting, SyncLogEntry, Notification,
} from './models/System';

// Runtime assertion: ensure migration chain covers schema version.
const migrationsList = (migrations as { sortedMigrations?: { toVersion: number }[] }).sortedMigrations;
if (migrationsList && migrationsList.length > 0) {
  const maxMigrationVersion = Math.max(...migrationsList.map((m) => m.toVersion));
  if (maxMigrationVersion !== schema.version) {
    throw new Error(
      `Schema/migration version mismatch: schema.version=${schema.version} but ` +
      `highest migration toVersion=${maxMigrationVersion}. Add the missing migration.`,
    );
  }
}

const MODEL_CLASSES = [
  // Domain 1: Core
  Merchant, Store, User, UserInvitation,
  // Domain 2: Products & Inventory
  Product, Category, ProductVariant, Batch,
  Godown, StockMovement, Supplier, PurchaseOrder, PoLineItem,
  SupplierPayment, PurchaseReturn, PurchaseReturnItem, SupplierLedger,
  StockTransfer, StockTransferItem,
  // Domain 3: Billing
  Invoice, LineItem, Payment, BillingSession,
  // Domain 4: Customers & Loyalty
  Customer, CustomerGroup, LoyaltyTier, LoyaltyTransaction,
  // Domain 5: Pricing & Promotions
  TaxProfile, PriceList, PriceListItem, Promotion,
  // Domain 6: GST & Compliance
  GstRateSlab, IndianState, HsnMaster, GstFiling, EInvoice, EWayBill,
  // Domain 7: Staff Management
  StaffAssignment, Shift, Attendance, Commission,
  // Domain 8: Finance
  ExpenseCategory, Expense,
  // Domain 9: Devices
  Device,
  // Domain 10: System
  AuditLog, Setting, SyncLogEntry, Notification,
];

/**
 * Message describing why the active store's database cannot be trusted, or null.
 *
 * Re-exported from `dbHealth` as a function rather than a mutable binding: the
 * callbacks below fire long after boot, and a module variable cannot tell React
 * to re-render. See db/dbHealth.ts.
 */
export { getDbError, setDbError, useDbHealthStore } from './dbHealth';

/** IndexedDB name for a store. Exported so teardown/diagnostics can address it. */
export function dbNameForStore(storeId: string): string {
  return `bigxbook-web-${storeId}`;
}

const instances = new Map<string, Database>();
let activeStoreId: string | null = null;

function createDatabase(storeId: string): Database {
  const adapter = new LokiJSAdapter({
    schema,
    migrations,
    // One IndexedDB per store — the store switcher relies on this isolation.
    dbName: dbNameForStore(storeId),
    // Worker disabled for the same reason as Desktop: WatermelonDB ships its
    // worker via a webpack-style worker-loader that Vite cannot resolve to a
    // constructor. In-thread matches Mobile (RN SQLite is in-thread too).
    useWebWorker: false,
    useIncrementalIndexedDB: true,

    // DURABILITY: LokiJS holds the database in memory and flushes to IndexedDB
    // on an autosave timer (WatermelonDB's default is 500ms). A reload or tab
    // close inside that window loses everything written since the last flush —
    // and because IncrementalIDBAdapter writes a batch atomically, an
    // interrupted save rolls the whole batch back rather than half-applying it.
    // Measured: syncing a full catalogue and reloading immediately lost all of
    // it; the same test with a pause first kept everything.
    //
    // 250ms halves the exposure. It cannot close it: there is no public API to
    // force a flush, and browsers do not await async IndexedDB writes during
    // unload. `beforeUnloadGuard()` below warns the operator instead.
    extraLokiOptions: {
      autosave: true,
      autosaveInterval: 250,
    },

    extraIncrementalIDBOptions: {
      // Another tab of this app overwrote our IndexedDB contents. For a till
      // that means two tabs are billing the same store and one has just lost
      // writes — loud, because it can destroy sales.
      onDidOverwrite: () => {
        const msg =
          'Another tab overwrote this store\'s local database. Changes made in ' +
          'this tab may already be lost. Close this tab and use a single tab per store.';
        console.error('[DB]', msg);
        ErrorTracking.captureMessage(msg, 'error');
        // Fatal, not advisory. Two tabs billing one store means writes from
        // here are landing in a database that is no longer authoritative, and
        // continuing would keep producing sales that quietly vanish.
        setDbError(msg);
      },
      // The underlying IndexedDB version changed, usually because another tab
      // deleted the database. Continuing to write here would be writing into a
      // database that no longer exists.
      onversionchange: () => {
        const msg = '[DB] IndexedDB version changed in another tab — reload required.';
        console.error(msg);
        ErrorTracking.captureMessage(msg, 'error');
        setDbError('The local database was changed in another tab. Please reload.');
      },
    },

    // Out of disk quota: WatermelonDB falls back to an in-memory database, so
    // billing appears to work while nothing is persisted. That is the worst
    // possible failure mode for an offline till, so treat it as fatal.
    onQuotaExceededError: (error: Error) => {
      const msg = `Storage full — local data cannot be saved: ${error?.message ?? error}`;
      setDbError(msg);
      console.error('[DB]', msg);
      ErrorTracking.captureException(error, { screen: 'DB', action: 'quota' }, 'fatal');
    },

    onSetUpError: (error: Error) => {
      const msg = error?.message || String(error);
      setDbError(msg);
      console.error('[DB] LokiJS setup error', error);
      ErrorTracking.captureException(error, { screen: 'DB', action: 'setup' }, 'fatal');
    },
  });

  return new Database({ adapter, modelClasses: MODEL_CLASSES });
}

/**
 * Makes `storeId` the active store, creating its Database on first use and
 * reusing it thereafter. Returns the Database so callers that want a direct
 * handle (rather than the `database` proxy) can hold one.
 */
export function activateStore(storeId: string): Database {
  if (!storeId) throw new Error('[DB] activateStore requires a storeId');
  let db = instances.get(storeId);
  if (!db) {
    db = createDatabase(storeId);
    instances.set(storeId, db);
  }
  // Clear only a previous store's setup failure. A quota or overwrite error
  // raised against THIS store is not resolved by re-activating it.
  if (activeStoreId !== storeId) setDbError(null);
  activeStoreId = storeId;
  return db;
}

/** The currently active store id, or null before `activateStore` is called. */
export function getActiveStoreId(): string | null {
  return activeStoreId;
}

/**
 * The CONCRETE active Database, not the `database` proxy.
 *
 * The proxy re-resolves the active store on every property access. That is what
 * makes ported modules work across a store switch, but it is wrong inside a
 * writer: `database.write(() => database.get(...).create(...))` opens the writer
 * on whichever database was active when `.write` was read, then runs the create
 * against whichever is active when `.get` is read. If the active store changes
 * in between, the create lands on a database with no open writer and
 * WatermelonDB throws "Collection.create() can only be called from inside of a
 * Writer".
 *
 * Capture this once and use the handle for the whole transaction.
 */
export function getActiveDatabase(): Database {
  return requireActive();
}

/**
 * Run a write against the CONCRETE active Database.
 *
 * Use this instead of `database.write(...)` anywhere the callback touches the
 * database again — which is almost always. See getActiveDatabase() above for
 * why re-reading the proxy inside a writer is unsafe.
 *
 *   await txn(async (db) => {
 *     const row = await db.get<Product>('products').find(id)
 *     await row.update(...)
 *   })
 */
export function txn<T>(fn: (db: Database) => Promise<T> | T): Promise<T> {
  const db = getActiveDatabase();
  return db.write(async () => fn(db));
}

/** True once a store has been activated and the proxy is safe to touch. */
export function isDatabaseReady(): boolean {
  return activeStoreId !== null;
}

function requireActive(): Database {
  if (!activeStoreId) {
    throw new Error(
      '[DB] No active store. Call activateStore(storeId) after login and store ' +
      'selection before touching the database.',
    );
  }
  // Non-null: activateStore always populates the map before setting the id.
  return instances.get(activeStoreId)!;
}

/**
 * Live handle to the active store's Database.
 *
 * Every property read and method call forwards to whichever store is active at
 * the moment of the call, so ported Desktop modules work unchanged across a
 * store switch. Methods are bound to the real Database so `this` stays correct.
 */
export const database: Database = new Proxy({} as Database, {
  get(_target, prop, _receiver) {
    const db = requireActive();
    const value = Reflect.get(db, prop, db);
    return typeof value === 'function' ? value.bind(db) : value;
  },
  set(_target, prop, value) {
    return Reflect.set(requireActive(), prop, value);
  },
  has(_target, prop) {
    return Reflect.has(requireActive(), prop);
  },
  getPrototypeOf() {
    return Object.getPrototypeOf(requireActive());
  },
});

/**
 * Warns before the tab closes while unsynced records exist.
 *
 * Two risks it covers, both specific to a browser till:
 *   • LokiJS may not have flushed the last few hundred milliseconds of writes
 *     to IndexedDB (see the autosave note in `createDatabase`).
 *   • Even flushed, unsynced sales exist only on this device, so closing the
 *     tab on a machine that is never reopened loses them.
 *
 * The count is read from a cache rather than queried in the handler, because
 * `beforeunload` must be synchronous — browsers ignore async work there.
 * Returns a disposer.
 */
let unsyncedCache = 0;

export function refreshUnsyncedCache(count: number): void {
  unsyncedCache = count;
}

export function installUnloadGuard(): () => void {
  const handler = (e: BeforeUnloadEvent) => {
    if (unsyncedCache <= 0) return;
    // Browsers show their own generic wording; the string is required to
    // trigger the prompt at all.
    e.preventDefault();
    e.returnValue = '';
    return '';
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}

/**
 * Asks the browser to make this origin's storage persistent, so IndexedDB is
 * not evicted under storage pressure. An evicted database on an offline till
 * means losing invoices that exist nowhere else (BIGXBOOK_WEB_PLAN §7.4).
 *
 * Returns whether persistence is granted. A `false` here is a strong signal
 * that this device should not be trusted for offline billing.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/**
 * Tables that carry business-critical data (invoices, payments, stock
 * movements). Used by `countUnsyncedRecords()` to warn before a destructive
 * reset — see BIGXBOOK_WEB_PLAN §7.1.
 */
const CRITICAL_TABLES = [
  'invoices', 'line_items', 'payments', 'products', 'product_variants',
  'batches', 'stock_movements', 'stock_transfers', 'stock_transfer_items',
  'purchase_orders', 'po_line_items', 'supplier_payments', 'purchase_returns',
  'customers', 'expenses',
] as const;

/**
 * Counts records with `is_synced = false` across all critical tables of the
 * active store. Any manual "clear local data" action must check this first:
 * a browser can hold invoices that exist nowhere else.
 */
/**
 * What is waiting to reach the server, and how long it has been waiting.
 *
 * A count alone is not enough to act on. Three unsynced invoices from twenty
 * seconds ago is a healthy till mid-cycle; three from this morning is a shop
 * whose sales exist in one browser and nowhere else. The age is what separates
 * them, so the two are gathered together and shown together.
 */
export interface PendingWork {
  count: number;
  /** Epoch ms of the oldest unsynced record, or null when there is none. */
  oldestAt: number | null;
}

export async function getPendingWork(): Promise<PendingWork> {
  try {
    // WatermelonDB's own record of what has not been pushed.
    //
    // NOT the `is_synced` COLUMN. That column is server-owned bookkeeping: the
    // server sets it true on its copy, and nothing flips the local one back, so
    // every row a till has ever billed stays `is_synced: false` on disk
    // forever. Counting it made the pending badge permanently non-zero on a
    // perfectly healthy shop — verified on a live till reading "6 not synced"
    // while all six rows carried `_status: 'synced'`.
    //
    // A warning that is always on is a warning nobody reads, which is worse
    // than not having one. `fetchLocalChanges` is what synchronize() itself
    // uses to decide what to send, so it cannot disagree with reality.
    const { affectedRecords } = await fetchLocalChanges(database);

    // Scoped to the shop's own data, as the original counter was.
    //
    // fetchLocalChanges reports EVERY table, and the sync writes its own
    // sync_logs row on the way out — so an accurate count of everything still
    // reads "1 not synced" on a till that has just finished syncing perfectly.
    // Telemetry is not the shopkeeper's problem; a badge that is never zero is
    // a badge nobody looks at.
    const critical = new Set<string>(CRITICAL_TABLES);
    const pending = affectedRecords.filter((r) => {
      const table = (r as unknown as { collection?: { table?: string }; table?: string });
      return critical.has(table.collection?.table ?? table.table ?? '');
    });

    let oldestAt: number | null = null;
    for (const record of pending) {
      const created = (record as unknown as { createdAt?: Date }).createdAt;
      const at = created instanceof Date ? created.getTime() : null;
      if (at != null && (oldestAt == null || at < oldestAt)) oldestAt = at;
    }

    refreshUnsyncedCache(pending.length);
    return { count: pending.length, oldestAt };
  } catch {
    // Never let a health read break the shell.
    return { count: 0, oldestAt: null };
  }
}

export async function countUnsyncedRecords(): Promise<number> {
  let total = 0;
  for (const table of CRITICAL_TABLES) {
    try {
      const collection = database.get(table);
      const unsynced = await collection
        .query(Q.where('is_synced', false))
        .fetchCount();
      total += unsynced;
    } catch {
      // Table may not exist yet on a fresh install — skip.
    }
  }
  refreshUnsyncedCache(total);
  return total;
}
